<template>
  <BaseModal
    :is-open="isOpen"
    title="Join Request"
    :closable="false"
    :show-close-button="false"
    max-width="500px"
    @close="handleDeny"
  >
    <!-- Player Info -->
    <div class="join-request-container">
      <div class="player-info">
        <!-- Profile Picture -->
        <div class="player-avatar">
          <img
            v-if="playerData?.profile_picture"
            :src="`/images/profiles/${playerData.profile_picture}.png`"
            :alt="playerData?.display_name || playerData?.username"
            class="avatar-image"
          />
          <div v-else class="avatar-initials">
            {{ getInitials(playerData?.display_name || playerData?.username || '?') }}
          </div>
        </div>

        <!-- Player Details -->
        <div class="player-details">
          <h3 class="player-name">
            {{ playerData?.display_name || playerData?.username || 'Unknown Player' }}
          </h3>
          <p class="player-username" v-if="playerData?.display_name && playerData?.username">
            @{{ playerData.username }}
          </p>
          <p class="request-message">wants to join your game</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="request-actions">
        <button @click="handleDeny" class="btn-deny" :disabled="responding">
          <span class="material-symbols-outlined">close</span>
          <span>Deny</span>
        </button>
        <button @click="handleApprove" class="btn-approve" :disabled="responding">
          <span v-if="responding" class="loading-spinner"></span>
          <span class="material-symbols-outlined" v-else>check</span>
          <span>Approve</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import BaseModal from './BaseModal.vue'
import type { JoinRequestData } from '@/composables/useModal'

/**
 * JoinRequestModal Component
 *
 * Displays when a player requests to join the game.
 * Shows player info and allows creator to approve or deny.
 *
 * Emits:
 * - approve: { sid, username, display_name, profile_picture }
 * - deny: { sid }
 * - close
 */

interface Props {
  isOpen: boolean
  playerData: JoinRequestData | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  playerData: null,
})

const emit = defineEmits<{
  approve: [data: JoinRequestData]
  deny: [data: { sid: string }]
  close: []
}>()

const responding = ref(false)

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Handle approve button
 */
async function handleApprove(): Promise<void> {
  if (!props.playerData || responding.value) return

  responding.value = true
  emit('approve', props.playerData)

  // Reset after delay to prevent double-click
  setTimeout(() => {
    responding.value = false
  }, 1000)
}

/**
 * Handle deny button
 */
function handleDeny(): void {
  if (!props.playerData || responding.value) return

  responding.value = true
  emit('deny', { sid: props.playerData.sid })
  emit('close')

  setTimeout(() => {
    responding.value = false
  }, 500)
}
</script>

<style scoped>
.join-request-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Player Info */
.player-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
}

.player-avatar {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(232, 197, 71, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  font-size: 1.75rem;
  font-weight: 700;
}

.player-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.player-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.player-username {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  font-style: italic;
}

.request-message {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0.5rem 0 0;
}

/* Actions */
.request-actions {
  display: flex;
  gap: 1rem;
}

.btn-approve,
.btn-deny {
  flex: 1;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-approve {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-approve:hover:not(:disabled) {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  transform: translateY(-2px);
}

.btn-approve:active:not(:disabled) {
  transform: translateY(0);
}

.btn-approve:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-deny {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-deny:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.btn-deny:active:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
}

.btn-deny:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .player-info {
    flex-direction: column;
    text-align: center;
  }

  .request-actions {
    flex-direction: column-reverse;
  }
}
</style>
